import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { ReportsService } from '@root/reports/services/reports.service';
import { HAZARD_MAP_KEYS } from '@root/reports/components/hazard-report/hazard-report.constant';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { NavigationService } from '@root/search/services/navigation.service';
import * as ReportsActions from './reports.actions';
import {
  DownloadCustomizedReport,
  DownloadHazardPDFMapSuccess,
  DownloadPropertyIntelligenceChartPdf,
  DownloadReportFailure,
  DownloadReportSuccess,
  EmailPropertyIntelligenceChart,
  GetClipApnSuccess,
  GetFloodMapReport,
  GetNeighborhoodProfileWithCriteria,
  GetNeighborsReport,
  GetNeighborsReportWithCriteria,
  SaveComparablesReportCriteria,
  SaveComparablesReportFailure,
  SaveNeighborhoodProfileCriteria,
  SaveNeighborhoodProfileCriteriaFailure,
  SaveNeighborhoodProfileCriteriaSuccess,
  SaveNeighborsReportCriteria,
  SaveNeighborsReportFailure,
  SetReportRateLimitType,
  SetSubHeaderButtons,
  StartComparableReportWithCriteria,
  StartComparablesReport,
  SubmitComparablesReportCriteria,
  SubmitNeighborsReportCriteria
} from './reports.actions';
import { combineLatest, Observable, of, timer } from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  delayWhen,
  filter,
  finalize,
  map,
  mergeMap,
  switchMap,
  exhaustMap,
  take,
  takeUntil,
  takeWhile,
  tap,
  withLatestFrom
} from 'rxjs/operators';

import { ActionName } from '@shared/models/effects-names.model';
import {
  Cell,
  ComparablesReportResponse,
  DocumentImageMessage,
  DocumentImageStatusResponse,
  DownloadReportModalData,
  LimitedReport,
  LoadableData,
  NeighborhoodProfileReport,
  NeighborsReportResponse,
  RATE_LIMIT_RETRY_TIME_SECONDS,
  Report,
  REPORT_RATE_LIMIT,
  ReportsPaymentResponse,
  ReportType,
  ReportTypeLink, SdpMarketTrends,
  Section,
  ZoningMapReport,
  AssessorMapReport,
  CommunityInsightsPDFLinks,
  OutputFlags,
  BuildingPermitsReport,
  ReportHeader,
  Chart,
  FreeCreditEligibility
} from '@store/reports/reports.model';
import { ConfirmationMessageType, ModalType } from '@shared/models/modal.model';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { EnsembleDialogService } from '@shared/dialog/ensemble-dialog.service';
import { CustomizeViewComponent } from '@root/reports/shared/components/customize-view/customize-view.component';
import { Action, select, Store } from '@ngrx/store';
import { AppState } from '@root/app-state.module';
import { selectIsNonDisclosure, selectPreferenceGroupByCode, selectPreferenceValue, selectPropIntelFilters, selectUserName } from '@store/user/user.selector';
import {
  ComponentCode,
  DisplayOptions,
  PreferenceCode,
  PreferenceElement,
  PreferenceGroup,
  UpdatePreference
} from '@store/user/user.model';
import {
  Email,
  FileLink,
  PropertyData,
  PropertyIdentifier,
  PropertyInformation,
  SelectionPropertiesItem
} from '@store/search-board/search-board.model';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import { ReportSearchCriteriaModalComponent } from '@root/reports/shared/components/report-search-criteria-modal/report-search-criteria-modal.component';
import {
  generateCharts,
  getClip,
  selectComparablesIsLoaded,
  selectComparablesPropertyInformationById,
  selectComparablesPropertySummaryList,
  selectComparablesReport,
  selectComparablesReportData,
  selectComparablesReportPreferenceGroup,
  selectComparablesSelectedProperties,
  selectComparablesSubjectProperty,
  selectDownloadedReportLinks,
  selectFloodMapReport,
  selectHazardPDFReport,
  selectHazardReport,
  selectMarketTrendsReportForPdf,
  selectNeighborhoodProfilePreferenceGroup,
  selectNeighborhoodProfileReport,
  selectNeighborhoodProfileReportForPdf,
  selectNeighborsInformation,
  selectNeighborsPreferenceGroup,
  selectPropertyDetailsReport,
  selectPropertyDetailsReportHeader,
  selectRateMapLimit,
  selectReportsFinished, selectSdpMarketTrends,
  selectSelectedPropertyIdentifier,
  selectSelectedPropertyInformation,
  selectSubHeaderHeader,
  selectTransactionID
} from '@store/reports/reports.selector';
import { PreferenceService } from '@root/search/services/preference.service';
import { SaveReportsPreferenceGroup } from '@store/user/user.actions';
import { PreferenceUtils } from '@shared/utils/preference-utils';
import { DownloadReportModalComponent } from '@root/reports/shared/components/download-report-modal/download-report-modal.component';
import { DownloadReportModalEnsembleComponent } from '@root/reports/shared/components/download-report-modal/download-report-modal-ensemble.component';
import { ConfirmationModalEnsembleComponent } from '@shared/components/confirmation-modal/confirmation-modal-ensemble.component';
import { ReportUtils } from '@shared/utils/report-utils';
import { SendEmail } from '@store/search-board/search-board.actions';
import { ReportDisplayFormatValue } from '@root/reports/shared/components/customize-view/customize-view.model';
import { ReportSearchCriteriaMultiGroupComponent } from '@root/reports/shared/components/report-search-criteria-multi-group/report-search-criteria-multi-group.component';
import { EmailModalComponent } from '@shared/components/email-modal/email-modal.component';
import { EmailModalEnsembleComponent } from '@shared/components/email-modal/email-modal-ensemble.component';
import { COUNTY_SUFFIX } from '@root/search/models/map.constants';
import { FormatterService } from '@shared/services/formatter.service';
import { MAX_COMPARABLES_REPORT_SELECTION } from '@root/reports/shared/constants/reports.constants';
import { PropertyUtil } from '@shared/utils/property-util';
import { ReverseLinkResponse } from '@root/search/models/map.models';
import { MapService } from '@root/search/services/map.service';
import { ComparablesPropertyDetailsModalComponent } from '@root/reports/components/comparables-report/comparables-report-edit/comparables-property-details-modal/comparables-property-details-modal.component';
import { ConfirmationService } from '@shared/services/confirmation.service';
import { MixpanelService } from '@shared/services/mixpanel.service';
import { ReportsMixPanelUtil } from '@root/reports/reports-mixpanel-util.module';
import { FloodMapCodes } from '@root/reports/components/flood-map-report/flood-map-report.constant';
import { HazardPDFReportLabels } from '@root/reports/components/hazard-report/hazard-report.constant';
import { PropertyIntelligenceService } from '@root/property-intelligence/services/property-intelligence.service';
import { DocumentImageModalComponent } from '@shared/components/document-image-modal/document-image-modal.component';
import { RedeemCreditOverlayComponent } from '@shared/components/redeem-credit-overlay/redeem-credit-overlay.component';
import { GetDocumentImageOffer } from '@store/orders/orders.actions';
import { PropertyIntelligenceFilter } from '@root/property-intelligence/models/property-intelligence-filter.model';
import { PropertyIntelligenceSearch } from '@root/property-intelligence/models/property-intelligence-search.model';
import { PropertyIntelligencePdfRequest } from '@root/property-intelligence/models/property-intelligence-pdf-request.model';
import { PropertyIntelligencePdfService } from '@root/property-intelligence/services/property-intelligence-pdf.service';

@Injectable()
export class ReportsEffects {
  private readonly IMG_BLOB_INDEX = 22;

  /**
   * Captures the (up to) 3 flood-map sections' images SEQUENTIALLY rather than concurrently.
   * Each capture rasterizes a live Google Map (html2canvas) plus runs a snapdom pass, and premium
   * pages have 2 such sections (flood + flash-flood) that live on the same page at once. Running
   * them in parallel (as `combineLatest` would) makes both maps fetch WMS tiles and render at the
   * same time, and one section's map can still be settling/loading when its snapshot is taken —
   * producing a blank map in the PDF. Sequencing them removes that contention so each section's
   * map has fully rendered before it's captured.
   */
  private captureFloodMapImages(payload: any): Observable<[string, any, string, string]> {
    return this.reportsService.convertFloodMapTemplateIntoImg(payload.commonMapSelector).pipe(
      concatMap((image: string) =>
        this.reportsService.convertFloodMapTemplateIntoImg(payload.floodPremiumMapSelector).pipe(
          concatMap((floodPremiumMapImage: string) =>
            this.reportsService.convertFloodMapTemplateIntoImg(payload.flashFloodPremiumMapSelector).pipe(
              map((flashFloodPremiumMapImage: string) => [image, payload, floodPremiumMapImage, flashFloodPremiumMapImage] as [string, any, string, string])
            )
          )
        )
      )
    );
  }

  constructor(
    private readonly actions$: Actions,
    private readonly reportsService: ReportsService,
    private readonly preferenceService: PreferenceService,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly ribbonService: RibbonService,
    private readonly formatterService: FormatterService,
    private readonly mapService: MapService,
    private readonly dialog: EnsembleDialogService,
    private readonly legacyDialog: MatDialog,
    private readonly store$: Store<AppState>,
    private readonly confirmationService: ConfirmationService,
    private readonly mixpanelService: MixpanelService,
    private readonly navigationService: NavigationService,
    private readonly propertyIntelligenceService: PropertyIntelligenceService,
    private readonly propertyIntelligencePdfService: PropertyIntelligencePdfService) {
    this.registerEnsembleModalConsoleToggle();
  }

  private registerEnsembleModalConsoleToggle(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const reportsWindow = window as Window & {
      __useEnsembleReportModals__?: boolean;
      em?: (useEnsemble?: boolean) => boolean;
    };

    /* Default off new modals */
    reportsWindow.__useEnsembleReportModals__ = true;

    reportsWindow.em = (useEnsemble?: boolean) => {
      if (typeof useEnsemble === 'boolean') {
        reportsWindow.__useEnsembleReportModals__ = useEnsemble;
      }

      return Boolean(reportsWindow.__useEnsembleReportModals__);
    };
  }

  private shouldUseEnsembleModals(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    return Boolean((window as Window & {
      __useEnsembleReportModals__?: boolean;
    }).__useEnsembleReportModals__);
  }

  getPropertyDetailsReport$: Observable<ReportsActions.GetPropertyDetailsReportSuccess | ReportsActions.GetPropertyDetailsReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.GET_PROPERTY_DETAILS_REPORT),
      this.filterCached(this.store$.pipe(select(selectPropertyDetailsReport))),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetPropertyDetailsReport) => {
        return this.reportsService.getPropertyDetailsReport(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: Report) => {
            return report ? new ReportsActions.GetPropertyDetailsReportSuccess(report) :
              new ReportsActions.GetPropertyDetailsReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    );
  });

  clearFlood$: Observable<ReportsActions.GetPropertyDetailsReportSuccess | ReportsActions.GetPropertyDetailsReportFailure> = createEffect(() => {
      return this.actions$.pipe(
        ofType(ReportsActions.CLEAR_FLOOD),
        this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetPropertyDetailsReport) => {
          return this.reportsService.getPropertyDetailsReport(action.payload).pipe(
            catchError(() => of(void (0))),
            map((report: Report) => {
              return report ? new ReportsActions.GetPropertyDetailsReportSuccess(report) :
                new ReportsActions.GetPropertyDetailsReportFailure();
            })
          );
        }, ActionName.GET_REPORT)
      );
  });

  $setPropertyDetailsReportHeader = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_CLIP_APN),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyInformation))),
      switchMap(([_, propInfo]) => {
        return this.reportsService.getClipFromAPN(propInfo.propertyIdentifier.apn, propInfo.propertyIdentifier.fipsCode)
          .pipe(map((clip) => {
            return new GetClipApnSuccess(clip);
          }));
      }))
  );

  downloadCommunityInsightsReport$: Observable<CommunityInsightsPDFLinks> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_COMMUNITY_INSIGHTS_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.DownloadCommunityInsightsReport) => {
        return this.reportsService.downloadCommunityInsightsReport(action.payload.communityInsightsParameters).pipe(
          catchError(() => of(void (0))),
          delay(10000),
          tap((pdfLink: CommunityInsightsPDFLinks) => {
            if (pdfLink) {
              const isPopupAllowed: Window = window.open(Object.values(pdfLink)[0]);
              if (!isPopupAllowed) {
                alert('Please ensure that pop-ups are allowed in your current browser to enable the download of reports.');
              }
              this.mixpanelService.track('Property - Download Document', {
                reportTab: 'Premium Neighborhood Reports',
                parcelId: action.payload.communityInsightsParameters.propertyIdentifier.parcelId,
                fipsCode: action.payload.communityInsightsParameters.propertyIdentifier.fipsCode,
                item: action.payload.communityInsightsParameters.products[0],
              });
              if (action.payload.isFreeLimit) {
                this.store$.dispatch(new ReportsActions.GetCommunityInsightsPdfLinks({
                  latitude: action.payload.communityInsightsParameters.propertyIdentifier.latitude.toString(),
                  longitude: action.payload.communityInsightsParameters.propertyIdentifier.longitude.toString(),
                  apn: action.payload.communityInsightsParameters.propertyIdentifier.apn,
                  address: null,
                  products: ['REST', 'SCHOOL', 'ECDEM', 'TRFO']
                }));
              }
            }
          }),
        )
      }, ActionName.DOWNLOAD_COMMUNITY_INSIGHTS_REPORT)
    )
  }, { dispatch: false });

  setUpFloodProperties$: Observable<ReportsActions.SetUpPropertiesSuccess> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.SET_UP_PROPERTIES),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SetUpProperties) => {
        return this.reportsService.getPropertyInformation(action.payload.propertyIdentifier).pipe(
          catchError(() => of(void (0))),
          map((property: any) => {
            if (action.payload.displayName === 'Standard Flood Map') {
              this.navigationService.openFloodMapReport();
            } else if(action.payload.displayName === 'Premium Flood Map') {
              this.navigationService.openPremiumFloodMapReport();
            } else if (action.payload.displayName === 'Hazard Report') {
              this.navigationService.openHazardReport();
            } else if(action.payload.displayName === 'Building Permits') {
              this.navigationService.openBuildingPermitsReport();
            } else if(action.payload.displayName === 'Assessor Map') {
              this.navigationService.openAssessorMapReport();
            } else if(action.payload.displayName === 'Foreclosure Report') {
              this.navigationService.openForeclosureReport();
            } else if(action.payload.displayName === 'Building Sketch Report') {
              this.navigationService.openBuildingSketchReport();
            }
            return new ReportsActions.SetUpPropertiesSuccess(property);
          })
        );
      }, ActionName.SET_UP_PROPERTIES)
    );
  });

  downloadPropertyDetailsReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_PROPERTY_DETAILS_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.DownloadPropertyDetailsReport) => {
        return this.reportsService.downloadPropertyDetailsReport(action.payload).pipe(
          catchError(() => of(void (0)))
        );
      }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadCustomizedPropertyDetailsReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_PROPERTY_DETAILS_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedPropertyDetailsReport) => {
        return this.reportsService.downloadPropertyDetailsReport(action.payload).pipe(
          this.handleCustomizedReport(ReportType.PROPERTY_DETAILS)
        );
      })
    );
  });

  emailPropertyDetailsReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_PROPERTY_DETAILS_REPORT),
      this.emailReport((action: ReportsActions.EmailPropertyDetailsReport) => {
        return this.reportsService.downloadPropertyDetailsReport(action.payload);
      })
    );
  });

  downloadComparablesReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_COMPARABLES_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.DownloadComparablesReport) => {
        return this.reportsService.downloadComparablesReport(action.payload).pipe(
          catchError(() => of(void (0))),
        );
      }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadCustomizedComparablesReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_COMPARABLES_REPORT),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE))),
        this.store$.pipe(select(selectComparablesReportPreferenceGroup)),
        this.store$.pipe(select(selectComparablesIsLoaded))
      ),
      switchMap(([action, preferenceGroup, searchCriteriaPreferenceGroup, isLoaded]:
                   [ReportsActions.DownloadCustomizedComparablesReport, PreferenceGroup, PreferenceGroup, boolean]) => {
        if (isLoaded) {
          return combineLatest([
            this.store$.pipe(select(selectComparablesSubjectProperty)),
            this.store$.pipe(select(selectComparablesSelectedProperties)),
            this.store$.pipe(select(selectComparablesPropertySummaryList))
          ])
            .pipe(
              take(1),
              map(([subjectProperty, selectedProperties, summaryList]:
                     [PropertyInformation, Array<PropertyInformation>, Array<PropertyInformation>]) => {
                const properties = selectedProperties && selectedProperties.length ? selectedProperties : summaryList;
                return [
                  subjectProperty,
                  ...properties
                ];
              })
            );
        }

        return this.reportsService.startComparablesReport(action.payload, preferenceGroup, searchCriteriaPreferenceGroup)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(ReportsActions.CANCEL_REPORTS_DOWNLOAD))),
            map((comparablesReport: ComparablesReportResponse) => {
              const subjectProperty = comparablesReport.compReportOutData.subjectProperty;
              const selectedProperties = PropertyUtil
                .getPropertySummaryList(comparablesReport.compReportOutData.resultData)
                .slice(0, MAX_COMPARABLES_REPORT_SELECTION);

              return [
                subjectProperty,
                ...selectedProperties
              ];
            }),
            catchError(() => of([]))
          );
      }),
      map((properties: Array<PropertyInformation>) => {
        return properties.map((propertyInformation: PropertyInformation) => propertyInformation.propertyIdentifier);
      }),
      switchMap((propertyIdentifiers: Array<PropertyIdentifier>) => {
        return this.reportsService.downloadComparablesReport({propertyIdentifiers: propertyIdentifiers}).pipe(
          this.handleCustomizedReport(ReportType.COMPARABLES)
        );
      })
    );
  });

  emailComparablesReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_COMPARABLES_REPORT),
      this.emailReport((action: ReportsActions.EmailComparablesReport) => {
        return this.reportsService.downloadComparablesReport(action.payload);
      })
    );
  });

  downloadForeclosureReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_FORECLOSURE_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.DownloadForeclosureReport) => {
        return this.reportsService.downloadForeclosureReport(action.payload).pipe(
          catchError(() => of(void (0)))
        );
      }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadCustomizedForeclosureReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_FORECLOSURE_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedForeclosureReport) => {
        return this.reportsService.downloadForeclosureReport(action.payload).pipe(
          this.handleCustomizedReport(ReportType.FORECLOSURE)
        );
      })
    );
  });

  emailForeclosureReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_FORECLOSURE_REPORT),
      this.emailReport((action: ReportsActions.EmailForeclosureReport) => {
        return this.reportsService.downloadForeclosureReport(action.payload);
      })
    );
  });

  downloadNeighborsReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_NEIGHBORS_REPORT),
      withLatestFrom(this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE)))),
      this.spinnerOverlayService.wrapRequest(([action, preferenceGroup]: [ReportsActions.DownloadNeighborsReport, PreferenceGroup]) => {
        return this.reportsService.downloadNeighborsReport(action.payload, preferenceGroup).pipe(
          catchError(() => of(void (0)))
        );
      }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadCustomizedNeighborsReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_NEIGHBORS_REPORT),
      withLatestFrom(this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE)))),
      switchMap(([action, preferenceGroup]: [ReportsActions.DownloadNeighborsReport, PreferenceGroup]) => {
        return this.reportsService.downloadNeighborsReport(action.payload, preferenceGroup).pipe(
          this.handleCustomizedReport(ReportType.NEIGHBORS)
        );
      })
    );
  });

  emailNeighborsReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_NEIGHBORS_REPORT),
      withLatestFrom(this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE)))),
      this.emailReport(([action, preferenceGroup]: [ReportsActions.EmailNeighborsReport, PreferenceGroup]) => {
        return this.reportsService.downloadNeighborsReport(action.payload, preferenceGroup);
      })
    );
  });

  downloadNeighborhoodProfileReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_NEIGHBORHOOD_PROFILE_REPORT),
      withLatestFrom(this.store$.pipe(select(selectNeighborhoodProfileReportForPdf))),
      this.spinnerOverlayService
        .wrapRequest(([propertyInformationAction, report]: [ReportsActions.DownloadNeighborhoodProfileReport, Report]) => {
          return this.reportsService.downloadNeighborhoodProfileReport(report, propertyInformationAction.payload.propertyData).pipe(
            catchError(() => of(void (0)))
          );
        }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadMarketTrendsPdfReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_MARKET_TRENDS_PDF_REPORT),
      concatLatestFrom(
        (action: ReportsActions.DownloadMarketTrendsPdfReport) => [
          this.store$.pipe(select(selectMarketTrendsReportForPdf)),
          this.store$.pipe(select(selectIsNonDisclosure(action.payload.propertyIdentifier.fipsCode)))
        ]
      ),
      this.spinnerOverlayService
        .wrapRequest(([downloadMarketTrendsAction, report, isNonDisclosure]: [ReportsActions.DownloadMarketTrendsPdfReport, Report, boolean]) => {

          this.selectMarketTrendsCharts(report, downloadMarketTrendsAction.chartSelected);

          const propertyInformation = downloadMarketTrendsAction.payload;
          let propertyAddress: string;
          try {
            propertyAddress = propertyInformation?.propertyData
              ? PropertyUtil.buildAddress(propertyInformation.propertyData, propertyInformation.propertyIdentifier)
              : undefined;
          } catch {
            propertyAddress = undefined;
          }
          const enrichedReport: Report = {
            ...report,
            header: {
              ...report.header,
              apn: propertyInformation?.propertyIdentifier?.apn || report.header?.apn,
              clip: propertyInformation?.propertyIdentifier?.clip || report.header?.clip,
              propertyAddress
            }
          };

          return this.reportsService.downloadMarketTrendsPdfReport(enrichedReport, downloadMarketTrendsAction.payload.propertyData, isNonDisclosure).pipe(
            catchError(() => of(void (0)))
          );
        }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadPropertyIntelligenceChartPdf$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_PROPERTY_INTELLIGENCE_CHART_PDF),
      switchMap((downloadPropertyIntelligencePdfAction: ReportsActions.DownloadPropertyIntelligenceChartPdf) => {
          return this.propertyIntelligenceService.generatePdf(downloadPropertyIntelligencePdfAction.payload)
          .pipe(catchError(() => of()));
      }),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  emailPropertyIntelligenceChart$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_PROPERTY_INTELLIGENCE_CHART),
      switchMap((action: ReportsActions.EmailPropertyIntelligenceChart) => {
        return this.propertyIntelligenceService.generatePdf(action.payload).pipe(
          map((fileLink: FileLink) => {
            const fileName = ReportUtils.getFileName(fileLink.fileLink);
            const reportType = fileLink.reportType;
            return new SendEmail({...action.email, fileName, reportType});
          })
        )
      })
    );
  });

  selectMarketTrendsCharts(report: Report, chartSelected: { [key: string]: string }) {
    if(report?.sections){
      report.sections.forEach(section => {
        if(section.charts){
          section.charts.forEach(chart => {
            const title = chartSelected[chart.title];
            const subtitle = chart.subTitle;
            chart.selected = (title && (title === subtitle)) || (!title && (subtitle === 'MLS'));
          });
        }
      });
    }
  }

  downloadCustomizedNeighborhoodProfileReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_NEIGHBORHOOD_PROFILE_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedNeighborhoodProfileReport) => {
        return this.reportsService.getNeighborhoodProfile(action.payload.propertyIdentifier)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(ReportsActions.CANCEL_REPORTS_DOWNLOAD))),
            catchError(() => of([])),
            map((response: NeighborhoodProfileReport) => [action.payload, generateCharts(response.report)])
          );
      }),
      switchMap(([propertyInformation, report]: [PropertyInformation, Report]) => {
        if (!propertyInformation || !report) {
          return of(new DownloadReportFailure({reportType: ReportType.NEIGHBORHOOD_PROFILE, fileLink: ''}));
        }
        return this.reportsService.downloadNeighborhoodProfileReport(report, propertyInformation.propertyData).pipe(
          this.handleCustomizedReport(ReportType.NEIGHBORS)
        );
      })
    );
  });

  emailNeighborhoodProfileReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_NEIGHBORHOOD_PROFILE_REPORT),
      withLatestFrom(this.store$.pipe(select(selectNeighborhoodProfileReportForPdf))),
      this.emailReport(([propertyInformationAction, report]: [ReportsActions.EmailNeighborhoodProfileReport, Report]) => {
        return this.reportsService.downloadNeighborhoodProfileReport(report, propertyInformationAction.payload.propertyData);
      })
    );
  });

  downloadHazardMapReport$: Observable<any> = createEffect(() => {
    // @ts-ignore
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_HAZARD_PDF_REPORT),
      withLatestFrom(
        this.store$.pipe(select(selectHazardPDFReport)),
        this.store$.pipe(select(selectSelectedPropertyInformation))
      ),
      switchMap(([data, images, property]: any) => {
        const hazardInformation: any = {}
        hazardInformation.mapList = [this.reportsService.getHazardReportDetail(data.payload.report.sections, HazardPDFReportLabels, property)];
        Object.keys(images).map((key: any) => {
          if (key) {
            hazardInformation[key] = images[key].image ? {
              image: images[key].image.substring(this.IMG_BLOB_INDEX),
              imageWidth: 812,
              imageHeight: 500,
              encodedImage: null,
            } : null
          }
        });

        return this.reportsService.downloadHazardReport(data.payload.propertyIdentifier, hazardInformation);
      }),
      switchMap((response: FileLink) => {
        this.spinnerOverlayService.hideSpinner(ActionName.HAZARD);
        return this.confirmDownload(response);
      })
    )
  }, { dispatch: false })

  downloadFloodMapReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_FLOOD_MAP_REPORT),
      exhaustMap((action: any) => {
        this.spinnerOverlayService.showSpinner(ActionName.GET_REPORT);

        return timer(100).pipe( // Delay just a little so the spinner has time to come up before we saturate the main thread with getting images.
          switchMap(() => this.captureFloodMapImages(action.payload)),
          concatMap(([image, payload, floodPremiumMapImage, flashFloodPremiumMapImage]: [string, any, any, any]) => {
            const floodMapInfo: any = {
              floodMapDetail: [this.reportsService.getReportDetail(payload, FloodMapCodes)],
              floodMapImage: {
                image: image.substring(this.IMG_BLOB_INDEX),
                imageWidth: 812,
                imageHeight: 500,
                encodedImage: null
              },
              validFloodMapPresent: true,
              standardFloodMap: payload.isStandard
            };

            if (floodPremiumMapImage) {
              floodMapInfo.floodRiskImage = {
                image: floodPremiumMapImage.substring(this.IMG_BLOB_INDEX),
                imageWidth: 812,
                imageHeight: 500,
                encodedImage: null
              };
            }

            if (flashFloodPremiumMapImage) {
              floodMapInfo.flashFloodRiskImage = {
                image: flashFloodPremiumMapImage.substring(this.IMG_BLOB_INDEX),
                imageWidth: 812,
                imageHeight: 500,
                encodedImage: null
              };
            }

            return this.reportsService.downloadFloodMapReport(
              payload.propertyIdentifier,
              floodMapInfo,
              payload.type
            ).pipe(
              catchError(() => of(void (0)))
            );
          }),
          finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.GET_REPORT))
        );
      }),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  // REWRITE
  downloadCustomizedFloodMapReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_FLOOD_MAP_REPORT),
      switchMap((action: any) => {
        return this.reportsService.getFloodMapImage()
          .pipe(
            map((mapImg: string) => [mapImg, action.payload])
          );
      }),
      switchMap(([image, payload]) => { // action: ReportsActions.DownloadCustomizedFloodMapReport
        const floodMapDetailValue = {};
        payload.floodMapDetail.forEach((section: Section) => {
          if (!section.multiColumn) {
            return;
          }

          section.multiColumn.forEach((col) => {
            col.row.forEach((row) => {
              const itemForSave = FloodMapCodes.find((code) => code.label === row.label);

              if (itemForSave) {
                floodMapDetailValue[itemForSave.code] = row.value;
              }
            });
          });
        });

        return this.reportsService.downloadFloodMapReport(
          payload.propertyIdentifier,
          {
            floodMapDetail: [floodMapDetailValue],
            floodMapImage: {
              image: image.substring(this.IMG_BLOB_INDEX),
              imageWidth: 812,
              imageHeight: 500,
              encodedImage: null
            },
            validFloodMapPresent: true
          }).pipe(
          this.handleCustomizedReport(ReportType.FLOODMAP)
        );
      })
    );
  });


  downloadPDFHazardMap$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_HAZARD_PDF_MAP),
      switchMap((action: ReportsActions.DownloadHazardPDFMap) => {
        const capture$ = action.payload === HAZARD_MAP_KEYS.SUMMARY
          ? this.reportsService.convertFloodMapTemplateIntoImg(action.payload)
          : this.reportsService.convertTemplateIntoImg(action.payload);
        return capture$
          .pipe(
            map((mapImg: string) => {
              return new DownloadHazardPDFMapSuccess({ image: mapImg, key: action.payload });
            })
          );
      }),
    );
  });

  downloadBuildingPermitsPdfReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_BUILDING_PERMITS_PDF_REPORT),
      this.spinnerOverlayService
        .wrapRequest((downloadBuildingPermitsAction: ReportsActions.DownloadBuildingPermitsPdfReport) => {
          return this.reportsService.downloadBuildingPermitsPdfReport(downloadBuildingPermitsAction.payload).pipe(
            catchError(() => of(void (0)))
          );
        }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  emailBuildingPermitsReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_BUILDING_PERMITS_REPORT),
      switchMap((action: ReportsActions.EmailBuildingPermitsReport) => {
        return this.reportsService.downloadBuildingPermitsPdfReport(action.payload).pipe(
          this.emailReport((file) => of(file))
        );
      })
    );
  });

  emailHazardReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_HAZARD_REPORT),
      withLatestFrom(
        this.store$.pipe(select(selectHazardPDFReport)),
        this.store$.pipe(select(selectSelectedPropertyInformation))
      ),
      switchMap(([data, images, property]: any) => {
        const hazardInformation: any = {}
        hazardInformation.mapList = [this.reportsService.getHazardReportDetail(data.payload.report.sections, HazardPDFReportLabels, property)];
        Object.keys(images).map((key: any) => {
          if (key) {
            hazardInformation[key] = images[key].image ? {
              image: images[key].image.substring(this.IMG_BLOB_INDEX),
              imageWidth: 812,
              imageHeight: 500,
              encodedImage: null,
            } : null
          }
        });
        return this.reportsService.downloadHazardReport(data.payload.propertyIdentifier, hazardInformation);
      }),
      this.emailReport((file) => of(file))
    );
  });

  emailFloodMapReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_FLOOD_MAP_REPORT),
      tap(() => this.spinnerOverlayService.showSpinner(ActionName.GET_REPORT)),
      delay(100), // Delay just a little so the spinner has time to come up before we saturate the main thread with getting images.
      switchMap((action: any) => {
        return this.captureFloodMapImages(action.payload);
      }),
      tap(() => this.spinnerOverlayService.hideSpinner(ActionName.GET_REPORT)),
      this.emailReport(([image, payload, floodPremiumMapImage, flashFloodPremiumMapImage]: [string, any, any, any]) => {
        const floodMapInfo: any = {
          floodMapDetail: [this.reportsService.getReportDetail(payload, FloodMapCodes)],
          floodMapImage: {
            image: image.substring(this.IMG_BLOB_INDEX),
            imageWidth: 812,
            imageHeight: 500,
            encodedImage: null
          },
          validFloodMapPresent: true,
          standardFloodMap: payload.isStandard
        };

        if (floodPremiumMapImage) {
          floodMapInfo.floodRiskImage = {
            image: floodPremiumMapImage.substring(this.IMG_BLOB_INDEX),
            imageWidth: 812,
            imageHeight: 500,
            encodedImage: null
          };
        }

        if (flashFloodPremiumMapImage) {
          floodMapInfo.flashFloodRiskImage = {
            image: flashFloodPremiumMapImage.substring(this.IMG_BLOB_INDEX),
            imageWidth: 812,
            imageHeight: 500,
            encodedImage: null
          };
        }

        return this.reportsService.downloadFloodMapReport(
          payload.propertyIdentifier,
          floodMapInfo,
          payload.type
        );
      })
    );
  });

  downloadBuildingSketchReport$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_BUILDING_SKETCH_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.DownloadBuildingSketchReport) => {
        return this.reportsService.downloadBuildingSketchReport(action.payload).pipe(
          catchError(() => of(void (0)))
        );
      }, ActionName.GET_REPORT),
      switchMap((response: FileLink) => {
        return this.confirmDownload(response);
      })
    );
  }, {dispatch: false});

  downloadCustomizedBuildingSketchReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_BUILDING_SKETCH_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedBuildingSketchReport) => {
        return this.reportsService.downloadBuildingSketchReport(action.payload).pipe(
          this.handleCustomizedReport(ReportType.BUILDING_SKETCH)
        );
      })
    );
  });

  downloadCustomizedBuildingPermitsReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_BUILDING_PERMITS_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedBuildingPermitsReport) => {
        return this.reportsService.getBuildingPermits(action.clip, action.apn, action.countyId)
          .pipe(
            takeUntil(this.actions$.pipe(ofType(ReportsActions.CANCEL_REPORTS_DOWNLOAD))),
            catchError(() => of({ buildingPermits: null })),
            map((response: BuildingPermitsReport) => [action.propertyInformation, action.clip, response])
          );
      }),
      switchMap(([propertyInformation, clip, report]: [PropertyInformation, string, BuildingPermitsReport]) => {
        if (!propertyInformation || !report.buildingPermits) {
          return of(new DownloadReportFailure({ reportType: ReportType.BUILDING_PERMITS, fileLink: '', exists: false }));
        }
        report.propertyInformation = {
          ...propertyInformation,
          propertyIdentifier: {
            ...propertyInformation.propertyIdentifier,
            clip: clip
          }
        };
        return this.reportsService.downloadBuildingPermitsPdfReport(report).pipe(
          this.handleCustomizedReport(ReportType.BUILDING_PERMITS)
        );
      })
    );
  });

  emailBuildingSketchReport$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_BUILDING_SKETCH_REPORT),
      this.emailReport((action: ReportsActions.EmailBuildingSketchReport) => {
        return this.reportsService.downloadBuildingSketchReport(action.payload);
      })
    );
  });

  downloadCustomizedPropertyPhotosReport$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_PROPERTY_PHOTOS_REPORT),
      switchMap((action: ReportsActions.DownloadCustomizedPropertyPhotosReport) => {
        return this.reportsService.downloadPropertyPhotosReport(action.payload.propertyData, action.payload.outputFlag).pipe(
          this.handleCustomizedReport(ReportType.PROPERTY_PHOTOS)
        );
      })
    );
  });

  startReportsDownload$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.START_REPORTS_DOWNLOAD),
      withLatestFrom(
        this.store$.pipe(select(selectSelectedPropertyInformation)),
        this.store$.pipe(select(selectFloodMapReport)),
        this.store$.pipe(select(getClip))
      ),
      switchMap(([action, propertyInformation, floodMapReport, clip]: [ReportsActions.StartReportsDownload, PropertyInformation, Report, string]) => {
        return action.payload.reportType.map((reportType: ReportType) => {
          switch (reportType) {
            case ReportType.PROPERTY_DETAILS:
              this.mixPanelServiceTrackingDownloadReports('Property Details', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedPropertyDetailsReport(propertyInformation.propertyIdentifier);
            case ReportType.COMPARABLES:
              this.mixPanelServiceTrackingDownloadReports('Comparables', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedComparablesReport(propertyInformation.propertyIdentifier);
            case ReportType.PROPERTY_PHOTOS:
              this.mixPanelServiceTrackingDownloadReports('Property Photos', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedPropertyPhotosReport({ propertyData: propertyInformation.propertyData, outputFlag: action.payload.outputFlag });
            case ReportType.FORECLOSURE:
              this.mixPanelServiceTrackingDownloadReports('Foreclosure', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedForeclosureReport(propertyInformation);
            case ReportType.FLOODMAP:
              this.mixPanelServiceTrackingDownloadReports('Flood Map', 'Property - Download Document', propertyInformation);
              // REWRITE
              return new ReportsActions
                .DownloadCustomizedFloodMapReport({
                  floodMapDetail: floodMapReport.sections,
                  propertyIdentifier: propertyInformation.propertyIdentifier,
                  isStandard: true
                });
            case ReportType.NEIGHBORS:
              this.mixPanelServiceTrackingDownloadReports('Neighbors', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedNeighborsReport(propertyInformation.propertyIdentifier);
            case ReportType.NEIGHBORHOOD_PROFILE:
              this.mixPanelServiceTrackingDownloadReports('Neighborhood Profile', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedNeighborhoodProfileReport(propertyInformation);
            case ReportType.BUILDING_SKETCH:
              this.mixPanelServiceTrackingDownloadReports('Building Sketch', 'Property - Download Document', propertyInformation);
              return new ReportsActions.DownloadCustomizedBuildingSketchReport(propertyInformation.propertyIdentifier);
            case ReportType.BUILDING_PERMITS:
              this.mixPanelServiceTrackingDownloadReports('Building Permits', 'Property - Download Document', propertyInformation);
              const propertyIdentifier = propertyInformation.propertyIdentifier;
              return new ReportsActions.DownloadCustomizedBuildingPermitsReport(clip, propertyIdentifier?.apn, propertyIdentifier.fipsCode, propertyInformation);
            default:
              return null;
          }
        })
          .filter(Boolean);
      }),
    );
  });

  downloadCustomizedReports$: Observable<DownloadReportSuccess | DownloadReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.DOWNLOAD_CUSTOMIZED_REPORT),
      switchMap((action: DownloadCustomizedReport) => {
        return this.store$.pipe(
          select(selectReportsFinished, action.payload),
          filter(Boolean),
          switchMap(() => this.store$.pipe(select(selectDownloadedReportLinks, action.payload))),
          take(1)
        );
      }),
      withLatestFrom(
        this.store$.pipe(
          select(selectSelectedPropertyInformation),
          filter(Boolean),
          map((propertyInformation: PropertyInformation) => propertyInformation.propertyData)
        )
      ),
      switchMap(
        ([reports, propertyData]: [ReportTypeLink, PropertyData]) => {
          return this.reportsService.downloadCustomizedReport({
            reports,
            propertyData
          })
            .pipe(this.handleCustomizedReport(ReportType.CUSTOMIZED));
        })
    );
  });

  emailCustomizedReports$: Observable<SendEmail> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.EMAIL_CUSTOMIZED_REPORT),
      this.emailReport((action: ReportsActions.EmailCustomizedReport) => {
        return of({fileLink: action.payload, reportType: ReportType.CUSTOMIZED});
      })
    );
  });

  openCustomizeViewReport$: Observable<any> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.OPEN_REPORT_CUSTOMIZE_VIEW),
      switchMap((action: ReportsActions.OpenReportCustomizeView) =>
        combineLatest(
          [
            this.store$.pipe(select(selectPreferenceGroupByCode(action.payload.preferenceCode))),
            this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_REPORTS_REPORT_CONFIG)))
          ]
        ).pipe(
          take(1),
          map(([reportSpecificPrefGroup, reportGeneralPrefGroup]:
                 [PreferenceGroup, PreferenceGroup]) => [action, reportSpecificPrefGroup, reportGeneralPrefGroup])
        )
      ),
      switchMap(([action, reportSpecificPrefGroup, reportGeneralPrefGroup]:
                   [ReportsActions.OpenReportCustomizeView, PreferenceGroup, PreferenceGroup]) => {
        const attributes = reportSpecificPrefGroup.preferenceElements.filter((element: PreferenceElement) => {
          return element.hasAccess;
        }).map((element: PreferenceElement) => ({
          selected: PreferenceUtils.hasPreferenceValue(element, DisplayOptions.YES),
          label: element.label,
          fieldCode: element.fieldCode,
          id: element.prefCode,
          name: element.label,
          fieldName: element.label
        }));

        const reportDisplayFormat = PreferenceUtils
          .getPreferenceElementValue(reportGeneralPrefGroup, PreferenceCode.PREF_REPORT_DISPLAY_FORMAT);

        const dialogRef = this.legacyDialog.open(CustomizeViewComponent, {
          panelClass: ModalType.LARGE,
          data: {
            title: 'Customize View',
            attributes,
            maxItemNumber: attributes.length,
            selectionRequired: action.payload.selectionRequired,
            reportDisplayFormat,
            preferenceCode: action.payload.preferenceCode
          }
        });

        return dialogRef.afterClosed().pipe(
          filter(Boolean),
          map((data: { selectedAttributes: Array<SelectionPropertiesItem>, reportDisplayFormat: ReportDisplayFormatValue }) => {
            const reportSpecificPrefElements = reportSpecificPrefGroup.preferenceElements
              .map((element: PreferenceElement) => {
                const newElement = this.copyElement(element);
                return data.selectedAttributes.some((item: SelectionPropertiesItem) => newElement.prefCode === item.id)
                  ? PreferenceUtils.setPreferenceValue(newElement, DisplayOptions.YES)
                  : PreferenceUtils.setPreferenceValue(newElement, DisplayOptions.NO);
              });

            const reportGeneralPrefElements = reportGeneralPrefGroup.preferenceElements
              .map((element: PreferenceElement) => {
                let newElement = this.copyElement(element);
                // Only overwrite the format when the dialog returned one; some reports hide the
                // format control and omit it, and writing undefined would corrupt the preference.
                if (newElement.prefCode === PreferenceCode.PREF_REPORT_DISPLAY_FORMAT && data.reportDisplayFormat != null) {
                  newElement = PreferenceUtils.setPreferenceValue(newElement, data.reportDisplayFormat);
                }
                return newElement;
              });

            return [
              {
                preferenceElementsList: reportSpecificPrefElements,
                prefGroupCode: reportSpecificPrefGroup.prefGroupCode,
                componentCode: ComponentCode.COMP_REPORTS
              },
              {
                preferenceElementsList: reportGeneralPrefElements,
                prefGroupCode: reportGeneralPrefGroup.prefGroupCode,
                componentCode: ComponentCode.COMP_REPORTS
              }
            ] as Array<UpdatePreference>;
          })
        );
      }),
      map((data: Array<UpdatePreference>) => new SaveReportsPreferenceGroup(data))
    );
  });

  getNeighborsReport$: Observable<ReportsActions.GetNeighborsReportSuccess | ReportsActions.GetNeighborsReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.GET_NEIGHBORS_REPORT),
      this.filterCached(this.store$.pipe(select(selectNeighborsInformation))),
      withLatestFrom(
        this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE))),
        this.store$.pipe(select(selectNeighborsPreferenceGroup))
      ),
      this.spinnerOverlayService.wrapRequest(([action, commonSearchPreferenceGroup, searchCriteriaPreferenceGroup]:
                                                [ReportsActions.GetNeighborsReport, PreferenceGroup, PreferenceGroup]) => {
        return this.reportsService.getNeighborsReport(action.payload, commonSearchPreferenceGroup, searchCriteriaPreferenceGroup).pipe(
          catchError(() => of(void (0))),
          map((report: NeighborsReportResponse) => {
            return report
              ? new ReportsActions.GetNeighborsReportSuccess(report)
              : new ReportsActions.GetNeighborsReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    );
  });

  getFloodMapReport$: Observable<ReportsActions.GetFloodMapReportSuccess | ReportsActions.GetFloodMapReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_FLOOD_MAP_REPORT),
      this.filterCached(this.store$.pipe(select(selectFloodMapReport))),
      withLatestFrom(this.store$.pipe(select(selectTransactionID))),
      this.spinnerOverlayService.wrapRequest(([action, id]: [ReportsActions.GetFloodMapReport, string]) => {
        return this.reportsService.getFloodMapReport(action.payload, id).pipe(
          catchError(() => of(void (0))),
          this.rateLimitCheck(action.payload),
          map((report: Report) => {
            if (report === null) {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Sorry, this property does not have the required location information to run this report.');
              this.disableButtons();
              return new ReportsActions.GetFloodMapReportFailure();
            }
            return report
              ? new ReportsActions.GetFloodMapReportSuccess(report)
              : new ReportsActions.GetFloodMapReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getStandardFloodMapReport$: Observable<ReportsActions.GetStandardFloodMapReportSuccess | ReportsActions.GetStandardFloodMapReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_STANDARD_FLOOD_MAP_REPORT),
      withLatestFrom(this.store$.pipe(select(selectTransactionID))),
      this.spinnerOverlayService.wrapRequest(([action, id]: [ReportsActions.GetStandardFloodMapReport, string]) => {
        return this.reportsService.getStandardFloodMapReport(action.payload, id).pipe(
          catchError(() => of(void (0))),
          this.rateLimitCheck(action.payload, (p) => new ReportsActions.GetStandardFloodMapReport(p)),
          map((report: Report) => {
            if (report === null) {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Sorry, this property does not have the required location information to run this report.');
              this.disableButtons();
              return new ReportsActions.GetStandardFloodMapReportFailure();
            }
            return report
              ? new ReportsActions.GetStandardFloodMapReportSuccess(report)
              : new ReportsActions.GetStandardFloodMapReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getHazardReport$: Observable<ReportsActions.GetHazardReportSuccess | ReportsActions.GetHazardReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_HAZARD_REPORT),
      this.filterCached(this.store$.pipe(select(selectHazardReport))),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyInformation))),
      this.spinnerOverlayService.wrapRequest(([action, propertyInformation]: [ReportsActions.GetHazardReport, PropertyInformation]) => {
        return this.reportsService.getHazardReport(action.payload, propertyInformation).pipe(
          catchError(() => of(void(0))),
          map((report: LimitedReport) => {
            if (report === null) {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Sorry, this property does not have the required location information to run this report.');
              this.disableButtons();
              return new ReportsActions.GetHazardReportFailure();
            }
            return report
              ? new ReportsActions.GetHazardReportSuccess(report)
              : new ReportsActions.GetHazardReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));


  getPartialHazardReport$: Observable<ReportsActions.GetPartialHazardReportSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_PARTIAL_HAZARD_REPORT),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyInformation))),
      switchMap(([action, propertyInformation]: [{ payload: {propertyIdentifier: PropertyIdentifier, optionalRow: Cell[]} }, PropertyInformation]) => {
        return this.reportsService.getHazardReport(action.payload.propertyIdentifier, propertyInformation, action.payload.optionalRow).pipe(
          catchError(() => of(void(0))),
          map((report: LimitedReport) => {
            if (report) {
              return new ReportsActions.GetPartialHazardReportSuccess(report);
            }
          })
        );
      })
    ));

  getSpatialBoundingBoxMapTile$: Observable<ReportsActions.SpatialBoundingBoxMapTileSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SPATIAL_BOUNDING_BOX_MAP_TILE),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SpatialBoundingBoxMapTile) => {
        return this.reportsService.getSpatialBoundingBoxMapTile(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: any) => {
            return new ReportsActions.SpatialBoundingBoxMapTileSuccess(report);
          })
        );
      }, ActionName.SPATIAL_BOUNDING_BOX_MAP_TILE)
    ));

  getSpatialSingleMapTile$: Observable<ReportsActions.SpatialSingleMapTileSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SPATIAL_SINGLE_MAP_TILE),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SpatialSingleMapTile) => {
        return this.reportsService.getSpatialSingleMapTile(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: any) => {
            return new ReportsActions.SpatialSingleMapTileSuccess(report);
          })
        );
      }, ActionName.SPATIAL_SINGLE_MAP_TILE)
    ));

  getSpatialGoogleNonCacheMapTile$: Observable<ReportsActions.SpatialGoogleNonCacheMapTileSuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SPATIAL_GOOGLE_NON_CACHE_MAP_TILE),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SpatialGoogleNonCacheMapTile) => {
        return this.reportsService.getSpatialGoogleNonCacheMapTile(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: any) => {
            return new ReportsActions.SpatialGoogleNonCacheMapTileSuccess(report);
          })
        );
      }, ActionName.SPATIAL_GOOGLE_NON_CACHE_MAP_TILE)
    ));

  startComparablesReport$: Observable<ReportsActions.StartComparablesReportSuccess | ReportsActions.StartComparablesReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.START_COMPARABLES_REPORT),
      this.filterCached(this.store$.pipe(select(selectComparablesReport))),
      withLatestFrom(this.store$.pipe(select(selectPreferenceGroupByCode(PreferenceCode.PG_COMMON_PREFERENCE)))),
      withLatestFrom(this.store$.pipe(select(selectComparablesReportPreferenceGroup))),
      this.spinnerOverlayService.wrapRequest(([[action, preferenceGroup], searchCriteriaPG]:
                                                [[ReportsActions.StartComparablesReport, PreferenceGroup], PreferenceGroup]) => {
        return this.reportsService.startComparablesReport(action.payload, preferenceGroup, searchCriteriaPG).pipe(
          catchError(() => of(void (0))),
          map((results: ComparablesReportResponse) => {
            return results ? new ReportsActions.StartComparablesReportSuccess(results) :
              new ReportsActions.StartComparablesReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getComparablesReport$: Observable<ReportsActions.GetComparablesReportSuccess | ReportsActions.GetComparablesReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_COMPARABLES_REPORT),
      this.filterCached(this.store$.pipe(select(selectComparablesReportData))),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetComparablesReport) => {
        return this.reportsService.getComparablesReport(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: Report) => {
            return report
              ? new ReportsActions.GetComparablesReportSuccess(report)
              : new ReportsActions.GetComparablesReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getForeclosureReport$: Observable<ReportsActions.GetForeclosureReportSuccess | ReportsActions.GetForeclosureReportFailure> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.GET_FORECLOSURE_REPORT),
      withLatestFrom(this.store$.pipe(select(selectTransactionID))),
      this.spinnerOverlayService.wrapRequest(([action, id]: [ReportsActions.GetForeclosureReport, string]) => {
        return this.reportsService.getForeclosureReport(action.payload, id).pipe(
          catchError(() => of(void (0))),
          map((results: Report) => {
            return results ? new ReportsActions.GetForeclosureReportSuccess(results) :
              new ReportsActions.GetForeclosureReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    );
  });

  getAssessorMapReport$: Observable<ReportsActions.GetAssessorMapReportSuccess | ReportsActions.GetAssessorMapReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_ASSESSOR_MAP_REPORT),
      withLatestFrom(this.store$.pipe(select(selectTransactionID))),
      this.spinnerOverlayService.wrapRequest(([action, id]: [ReportsActions.GetAssessorMapReport, string]) => {
        return this.reportsService.getAssessorMapReport(action.payload, id).pipe(
          catchError(() => of(void (0))),
          map((report: AssessorMapReport) => {
            return report ? new ReportsActions.GetAssessorMapReportSuccess(report) :
              new ReportsActions.GetAssessorMapReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getZoningMapReport$: Observable<ReportsActions.GetZoningMapReportSuccess | ReportsActions.GetZoningMapReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_ZONING_MAP_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetZoningMapReport) => {
        return this.reportsService.getZoningMapReport(action.payload).pipe(
          catchError(() => of(void (0))),
          map((report: ZoningMapReport) => {
            return report ? new ReportsActions.GetZoningMapReportSuccess(report) :
              new ReportsActions.GetZoningMapReportFailure();
          })
        );
      }, ActionName.GET_REPORT)
    ));

  openComparablesCriteriaModal$: Observable<SubmitComparablesReportCriteria | StartComparableReportWithCriteria> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.OPEN_COMPARABLES_CRITERIA_MODAL),
      withLatestFrom(this.store$.pipe(select(selectComparablesReportPreferenceGroup))),
      switchMap(([_, preferenceGroup]) => {
        const dialogRef = this.legacyDialog.open(ReportSearchCriteriaModalComponent, {
          panelClass: ModalType.SCROLLABLE.concat(ModalType.SMALL).concat('no-scroll-container'),
          data: {preferenceGroup: preferenceGroup.childPreferenceGroups[0], title: 'Comparables Search Criteria'}
        });
        return dialogRef.afterClosed().pipe(filter(Boolean), map(
          (result: { elements: Array<PreferenceElement>, save: boolean }) => {
            return result.save ? new SubmitComparablesReportCriteria(result.elements) :
              new StartComparableReportWithCriteria(result.elements);
          }));
      })
    ));

  openNeighborsReportCriteriaModal$: Observable<SubmitNeighborsReportCriteria | GetNeighborsReportWithCriteria> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.OPEN_NEIGHBORS_REPORT_CRITERIA_MODAL),
      withLatestFrom(this.store$.pipe(select(selectNeighborsPreferenceGroup))),
      switchMap(([_, preferenceGroup]) => {
        const dialogRef = this.legacyDialog.open(ReportSearchCriteriaModalComponent, {
          panelClass: ModalType.SCROLLABLE.concat(ModalType.SMALL),
          data: {preferenceGroup: preferenceGroup.childPreferenceGroups[0], title: 'Neighbors Search Criteria'}
        });
        return dialogRef.afterClosed().pipe(filter(Boolean), map(
          (result: { elements: Array<PreferenceElement>, save: boolean }) => {
            return result.save ? new SubmitNeighborsReportCriteria(result.elements) :
              new GetNeighborsReportWithCriteria(result.elements);
          }));
      })
    ));

  openNeighborhoodProfileCriteriaModal$: Observable<SaveNeighborhoodProfileCriteria | GetNeighborhoodProfileWithCriteria> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.OPEN_NEIGHBORHOOD_PROFILE_CRITERIA_MODAL),
      withLatestFrom(this.store$.pipe(select(selectNeighborhoodProfilePreferenceGroup))),
      switchMap(([_, preferenceGroup]) => {
        const dialogRef = this.legacyDialog.open(ReportSearchCriteriaMultiGroupComponent, {
          panelClass: ModalType.SCROLLABLE.concat(ModalType.SMALL),
          data: {preferenceGroups: preferenceGroup.childPreferenceGroups, title: 'Neighborhood Profile Search Criteria'}
        });
        return dialogRef.afterClosed().pipe(filter(Boolean), map(
          (result: { updatePreferences: Array<UpdatePreference | PreferenceGroup>, save: boolean }) => {
            if (result.save) {
              const update = result.updatePreferences.map((updatePreference: UpdatePreference) => {
                updatePreference.componentCode = ComponentCode.COMP_REPORTS;
                return updatePreference;
              });
              return new SaveNeighborhoodProfileCriteria(update);
            }
            return new GetNeighborhoodProfileWithCriteria(result.updatePreferences as Array<PreferenceGroup>);
          }));
      })
    ));

  getNeighborhoodProfileWithCriteria$: Observable<ReportsActions.GetNeighborhoodProfileWithCriteriaSuccess |
    ReportsActions.GetNeighborhoodProfileFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_NEIGHBORHOOD_PROFILE_WITH_CRITERIA),
    withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier)),
      this.store$.pipe(select(selectNeighborhoodProfilePreferenceGroup))),
    this.spinnerOverlayService.wrapRequest(([action, propertyIdentifier, preferenceGroup]) => {
      const newPreferenceGroup: PreferenceGroup = {
        ...preferenceGroup,
        childPreferenceGroups: (action as GetNeighborhoodProfileWithCriteria).payload
      };
      return this.reportsService.getNeighborhoodProfileWithCriteria(propertyIdentifier, newPreferenceGroup)
        .pipe(
          catchError(() => of(void (0))),
          map((results: Report) => {
            return results ? new ReportsActions.GetNeighborhoodProfileWithCriteriaSuccess(results) :
              new ReportsActions.GetNeighborhoodProfileFailure();
          })
        );
    }, ActionName.GET_REPORT)
  ));

  saveNeighborhoodProfileCriteria: Observable<SaveNeighborhoodProfileCriteriaSuccess | SaveNeighborhoodProfileCriteriaFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SAVE_NEIGHBORHOOD_PROFILE_CRITERIA),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SaveNeighborhoodProfileCriteria) => {
        return this.preferenceService.savePreferenceGroupSettings(action.payload).pipe(
          catchError(() => {
            this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Something went wrong. Try to save preference again.');
            return of(null);
          }),
          map((result) => {
            return Boolean(result) ? new SaveNeighborhoodProfileCriteriaSuccess() : new SaveNeighborhoodProfileCriteriaFailure();
          }));
      }, ActionName.GET_REPORT)
    ));

  submitComparablesCriteria$: Observable<SaveComparablesReportCriteria | StartComparablesReport | SaveComparablesReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SUBMIT_COMPARABLES_REPORT_CRITERIA),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SaveComparablesReportCriteria) => {
        return this.preferenceService.saveComparablesSearchCriteria(action.payload).pipe(
          catchError(() => of(void (0))),
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          switchMap(([results, selectedPropertyIdentifier]) => {
            return results ? [new SaveComparablesReportCriteria(action.payload),
                new StartComparablesReport(selectedPropertyIdentifier)] :
              of(new ReportsActions.SaveComparablesReportFailure());
          })
        );
      }, ActionName.GET_REPORT)
    ));

  startComparablesReportWithCriteria$: Observable<SaveComparablesReportCriteria | StartComparablesReport> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.START_COMPARABLES_REPORT_WITH_CRITERIA),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
      mergeMap(([action, selectedPropertyIdentifier]: [ReportsActions.StartComparableReportWithCriteria, PropertyIdentifier]) => {
        return [new SaveComparablesReportCriteria(action.payload),
          new StartComparablesReport(selectedPropertyIdentifier)];
      })
    ));

  getNeighborsReportWithCriteria$: Observable<SaveNeighborsReportCriteria | GetNeighborsReport> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_NEIGHBORS_REPORT_WITH_CRITERIA),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
      mergeMap(([action, selectedPropertyIdentifier]: [ReportsActions.GetNeighborsReportWithCriteria, PropertyIdentifier]) => {
        return [new SaveNeighborsReportCriteria(action.payload), new GetNeighborsReport(selectedPropertyIdentifier)];
      })
    ));

  submitNeighborsReportCriteria$: Observable<SaveNeighborsReportCriteria | GetNeighborsReport | SaveNeighborsReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.SUBMIT_NEIGHBORS_REPORT_CRITERIA),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.SaveNeighborsReportCriteria) => {
        return this.preferenceService.saveNeighborsSearchCriteria(action.payload).pipe(
          catchError(() => of(void (0))),
          withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
          switchMap(([results, selectedPropertyIdentifier]) => {
            return results ? [new SaveNeighborsReportCriteria(action.payload),
                new GetNeighborsReport(selectedPropertyIdentifier)] :
              of(new ReportsActions.SaveNeighborsReportFailure());
          })
        );
      }, ActionName.GET_REPORT)
    ));

  getNeighborhoodProfile$: Observable<ReportsActions.GetNeighborhoodProfileSuccess |
    ReportsActions.GetNeighborhoodProfileFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_NEIGHBORHOOD_PROFILE),
    this.filterCached(this.store$.pipe(select(selectNeighborhoodProfileReport))),
    this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetNeighborhoodProfile) => {
      return this.reportsService.getNeighborhoodProfile(action.payload).pipe(
        catchError(() => of(void (0))),
        map((results: Report) => {
          return results ? new ReportsActions.GetNeighborhoodProfileSuccess(results) :
            new ReportsActions.GetNeighborhoodProfileFailure();
        })
      );
    }, ActionName.GET_REPORT)
  ));

  getBuildingSketch$: Observable<ReportsActions.GetBuildingSketchSuccess | ReportsActions.GetBuildingSketchFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_BUILDING_SKETCH),
    withLatestFrom(this.store$.pipe(select(selectTransactionID))),
    this.spinnerOverlayService.wrapRequest(([action, id]: [ReportsActions.GetBuildingSketch, string]) => {
      return this.reportsService.getBuildingSketch(action.payload, id).pipe(
        catchError(() => of(void (0))),
        map((report: Report) => {
          return report ? new ReportsActions.GetBuildingSketchSuccess(report) :
            new ReportsActions.GetBuildingSketchFailure();
        })
      );
    }, ActionName.GET_REPORT)
  ));

  getSdpMarketTrends$: Observable<ReportsActions.GetSdpMarketTrendsSuccess | ReportsActions.GetSdpMarketTrendsFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_SDP_MARKET_TRENDS),
    this.filterCached(this.store$.pipe(select(selectSdpMarketTrends))),
    this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetSdpMarketTrends) => {
      return this.reportsService.getSdpMarketTrends(action.payload).pipe(
        catchError(() => of(void (0))),
        map((report: SdpMarketTrends) => {
          return report ? new ReportsActions.GetSdpMarketTrendsSuccess(report) :
            new ReportsActions.GetSdpMarketTrendsFailure();
        })
      );
    }, ActionName.GET_REPORT)
  ));

  getCommunityInsightsReport$: Observable<ReportsActions.GetCommunityInsightsPdfLinksSuccess | ReportsActions.GetCommunityInsightsPdfLinksFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_COMMUNITY_INSIGHTS_PDF_LINKS),
    this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetCommunityInsightsPdfLinks) => {
      return this.reportsService.getCommunityInsightsReport(action.payload).pipe(
        catchError(() => of(void (0))),
        map((report: CommunityInsightsPDFLinks) => {
          return report ? new ReportsActions.GetCommunityInsightsPdfLinksSuccess(report) :
            new ReportsActions.GetCommunityInsightsPdfLinksFailure();
        })
      );
    }, ActionName.GET_REPORT)
  ));

  getReportsPaymentStatus$: Observable<ReportsActions.GetReportsPaymentStatusSuccess> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_REPORTS_PAYMENT_STATUS),
    withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier))),
    this.spinnerOverlayService.wrapRequest(
      ([action, propertyIdentifier]: [ReportsActions.GetReportsPaymentStatus, PropertyIdentifier]) => {
        return this.reportsService.getPurchaseStatuses({
          reports: action.payload,
          propertyIdentifier
        }).pipe(
          catchError(() => of(void (0))),
          filter(Boolean),
          map((paymentStatus: ReportsPaymentResponse) => {
            return new ReportsActions.GetReportsPaymentStatusSuccess(paymentStatus);
          })
        );
      }, ActionName.GET_REPORT)
  ));

  getLimitStateSuccess$: Observable<ReportsActions.CheckLimitStateSuccess> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.CHECK_LIMIT_STATE),
    switchMap(() =>
      timer(5000, 10000).pipe(
        withLatestFrom(this.store$.pipe(select(selectRateMapLimit))),
        takeWhile(([_, limit]) => limit !== 429),
        switchMap(() =>
          this.reportsService.getLimitMap()
            .pipe(
              map((limit) => new ReportsActions.CheckLimitStateSuccess(limit)),
              catchError(() => {
                return of(null);
              })
            )
        )
      )
    )), {dispatch: false});

  getDocumentImageStatus$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_DOCUMENT_IMAGE_STATUS),
    withLatestFrom(
      this.store$.pipe(select(selectSelectedPropertyInformation)),
      this.store$.pipe(select(selectPropertyDetailsReportHeader))
    ),
    switchMap(([action, propertyInformation, propertyDetailsHeader]: [ReportsActions.GetDocumentImageStatus, PropertyInformation, ReportHeader]) => {
      const docNum = this.getDocumentNumber(action.payload);
      const resolvedIdentifier = this.resolvePropertyIdentifierApn(propertyInformation?.propertyIdentifier, propertyDetailsHeader);
      return this.reportsService.checkDocumentImageFreeCreditEligibility(
        action.payload, docNum, resolvedIdentifier, action.isFreeLimitReport
      ).pipe(
        catchError(() => of({ eligible: false, availableLimit: 0, totalLimit: 0 } as FreeCreditEligibility)),
        switchMap((eligibility: FreeCreditEligibility) => {
          if (eligibility.eligible) {
            const dialogRef = this.legacyDialog.open<RedeemCreditOverlayComponent, { availableCredits: number; showCloseButton?: boolean }>(
              RedeemCreditOverlayComponent,
              {
                panelClass: ModalType.SMALL,
                disableClose: true,
                data: { availableCredits: eligibility.availableLimit, showCloseButton: true }
              }
            );

            return dialogRef.afterClosed().pipe(
              switchMap((confirmed: boolean) => {
                if (confirmed) {
                  return this.fetchDocumentImageWithSpinner(action, propertyInformation, propertyDetailsHeader);
                }
                return of(null);
              })
            );
          }
          return this.fetchDocumentImageWithSpinner(action, propertyInformation, propertyDetailsHeader);
        })
      );
    })
  ), {dispatch: false});

  private fetchDocumentImageWithSpinner(
    action: ReportsActions.GetDocumentImageStatus,
    propertyInformation: PropertyInformation,
    subheaderHeader?: ReportHeader
  ): Observable<any> {
    this.spinnerOverlayService.showSpinner(ActionName.GET_REPORT);
    return this.fetchDocumentImage(action, propertyInformation, subheaderHeader).pipe(
      finalize(() => this.spinnerOverlayService.hideSpinner(ActionName.GET_REPORT))
    );
  }

  private fetchDocumentImage(
    action: ReportsActions.GetDocumentImageStatus,
    propertyInformation: PropertyInformation,
    subheaderHeader?: ReportHeader
  ): Observable<any> {
    const resolvedIdentifier = this.resolvePropertyIdentifierApn(propertyInformation?.propertyIdentifier, subheaderHeader);
    return this.reportsService.getDocumentImageStatus(
      action.payload, this.getDocumentNumber(action.payload),
      resolvedIdentifier, action.isFreeLimitReport
    ).pipe(
      catchError(() => of({})),
      tap((response: DocumentImageStatusResponse) => {
        if (response && response.ecommerceOutputInfo) {
          this.store$.dispatch(new GetDocumentImageOffer(this.getDocumentNumber(action.payload)));

          this.legacyDialog.open(DocumentImageModalComponent, {
            panelClass: ModalType.SCROLLABLE.concat(ModalType.SMALL).concat('scrollable-modal'),
            data: {
              reportExists: true,
              documentNumber: this.getDocumentNumber(action.payload),
              params: action.payload,
              reportAvailableToView: false,
              propertyInformation: propertyInformation
            }
          })
          return;
        }

        if (response && response.status === 'SUCCESS') {
          window.open(`/api/load-document-image?${action.payload}`);
        } else {
          switch (response.message) {
            case DocumentImageMessage.exceptionerror:
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'An exception occurred when retrieving document image file.');
              break;
            case DocumentImageMessage.exceedusage:
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Limit has been exceeded. No document image available for view.');
              break;
            case DocumentImageMessage.nodocimage:
            default:
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'There is no document image available.');
          }
        }
      }),
    );
  }

  openReportCustomizeDownloadModal$: Observable<ReportsActions.CancelReportsDownload> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.OPEN_REPORT_CUSTOMIZE_DOWNLOAD),
    withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier)), this.store$.pipe(select(selectSubHeaderHeader))),
    switchMap(([_, propertyIdentifier, header]: [ReportsActions.OpenReportCustomizeDownload, PropertyIdentifier, ReportHeader]) => {
      const modalData: DownloadReportModalData = {
        title: 'Customize Print',
        download: 'Please select the reports to be included in PDF file.',
        mainAction: 'Print',
        outputFlag: OutputFlags.PRINT,
        propertyIdentifier: propertyIdentifier,
        address: header.address
      };

      return this.shouldUseEnsembleModals()
        ? this.dialog
          .open(DownloadReportModalEnsembleComponent, {
            data: modalData,
          })
          .afterClosed$
        : this.legacyDialog
          .open<DownloadReportModalComponent, DownloadReportModalData>(DownloadReportModalComponent, {
            panelClass: ModalType.SMALL,
            data: modalData,
          })
          .afterClosed();
    }),
    map((fileLink: string) => {
      if (fileLink) {
        this.downloadPdfReport({fileLink, reportType: ReportType.CUSTOMIZED});
      } else {
        return new ReportsActions.CancelReportsDownload();
      }
    }),
    filter<ReportsActions.CancelReportsDownload>(Boolean)
  ));

  openReportCustomizeEmailModal$: Observable<ReportsActions.EmailCustomizedReport | ReportsActions.CancelReportsDownload>
    = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.OPEN_REPORT_CUSTOMIZE_EMAIL),
    withLatestFrom(this.store$.pipe(select(selectSelectedPropertyIdentifier)), this.store$.pipe(select(selectSubHeaderHeader))),
    switchMap(([_, propertyIdentifier, header]: [ReportsActions.OpenReportCustomizeDownload, PropertyIdentifier, ReportHeader]) => {
      const modalData: DownloadReportModalData = {
        title: 'Customize Email',
        download: 'Please select the reports to be included in email.',
        mainAction: 'Add to Email',
        outputFlag: OutputFlags.EMAIL,
        propertyIdentifier: propertyIdentifier,
        address: header.address,
      };

      return this.shouldUseEnsembleModals()
        ? this.dialog
          .open(DownloadReportModalEnsembleComponent, {
            data: modalData,
          })
          .afterClosed()
        : this.legacyDialog
          .open<DownloadReportModalComponent, DownloadReportModalData>(DownloadReportModalComponent, {
            panelClass: ModalType.SMALL,
            data: modalData,
          })
          .afterClosed();
    }),
    map((fileLink: string) => {
      return fileLink
        ? new ReportsActions.EmailCustomizedReport(fileLink)
        : new ReportsActions.CancelReportsDownload();
    })
  ));

  openComparablesPropertyDetailsModal$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.OPEN_COMPARABLES_PROPERTY_DETAILS_MODAL),
    switchMap((action: ReportsActions.OpenComparablesPropertyDetailsModal) => {
      return this.legacyDialog.open(ComparablesPropertyDetailsModalComponent, {
        panelClass: ModalType.LARGE,
        data: action.payload
      }).afterClosed();
    })
  ), {dispatch: false});

  getComparablesPropertyDetailsReport$: Observable<ReportsActions.GetComparablesPropertyDetailsReportSuccess |
    ReportsActions.GetComparablesPropertyDetailsReportFailure> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_COMPARABLES_PROPERTY_DETAILS_REPORT),
    this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetComparablesPropertyDetailsReport) => {
      return this.reportsService.getPropertyDetailsReport(action.payload, false).pipe(
        catchError(() => of(void (0))),
        map((results: Report) => {
          return results ? new ReportsActions.GetComparablesPropertyDetailsReportSuccess(results) :
            new ReportsActions.GetComparablesPropertyDetailsReportFailure();
        })
      );
    }, ActionName.GET_REPORT)
  ));

  getComparablesReverseLinkGrid: Observable<ReverseLinkResponse> = createEffect(() => this.actions$.pipe(
    ofType(ReportsActions.GET_COMPARABLES_REVERSE_LINK_GRID),
    this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetComparablesReverseLinkGrid) => {
      return this.store$.pipe(take(1), select(selectPreferenceGroupByCode(PreferenceCode.PG_RL_REVERSELINK)),
        withLatestFrom(this.store$.pipe(select(selectComparablesPropertyInformationById(action.payload)))),
        switchMap(([preferenceGroup, propertyInformation]: [PreferenceGroup, PropertyInformation]) => {
          return this.mapService.getReverseLinkGrid(preferenceGroup, propertyInformation).pipe(
            tap((result: ReverseLinkResponse) => {
              result && result.reverseLinkURL ?
                window.open(result.reverseLinkURL, '_blank') :
                this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Reverse link error or link does not exist');
            }),
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Reverse link error or link does not exist');
              return of(void (0));
            })
          );
        }));
    }, ActionName.GET_PROPERTY_INFORMATION)
  ), {dispatch: false});

  getBuildingPermitsReportAvailabilty$: Observable<ReportsActions.GetBuildingPermitsReportAvailabilitySuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_BUILDING_PERMITS_REPORT_AVAILABILITY),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetBuildingPermitsReportAvailability) => {
        return this.reportsService.getBuildingPermitsAvailability(
          action.payload.clip,
          action.payload.apn,
          action.payload.countyId,
        ).pipe(
          catchError(() => of(void (0))),
          map((isAvailable: boolean) => new ReportsActions.GetBuildingPermitsReportAvailabilitySuccess(isAvailable))
        )
      }, ActionName.GET_BUILDING_PERMITS_REPORT_AVAILABILITY)
    )
  );

  getBuildingSketchReportAvailabilty$: Observable<ReportsActions.GetBuildingSketchReportAvailabilitySuccess> = createEffect(() =>
    /* RP-9442 Always showing tab for availability, only call actual building sketch endpoint on report page */
    this.actions$.pipe(
      ofType(ReportsActions.GET_BUILDING_SKETCH_REPORT_AVAILABILITY),
      map(() => new ReportsActions.GetBuildingSketchReportAvailabilitySuccess(true))
    )
  );

  getForeclosureReportAvailabilty$: Observable<ReportsActions.GetForeclosureReportAvailabilitySuccess> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_FORECLOSURE_REPORT_AVAILABILITY),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetForeclosureReportAvailability) => {
        return this.reportsService.getForeclosureReportAvailability(action.payload.propertyIdentifier).pipe(
          catchError(() => of(void (0))),
          map((isAvailable: boolean) => new ReportsActions.GetForeclosureReportAvailabilitySuccess(isAvailable))
        )
      }, ActionName.GET_FORECLOSURE_REPORT_AVAILABILITY)
    )
  );

  getBuildingPermitsReport$: Observable<ReportsActions.GetBuildingPermitsReportSuccess | ReportsActions.GetBuildingPermitsReportFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.GET_BUILDING_PERMITS_REPORT),
      this.spinnerOverlayService.wrapRequest((action: ReportsActions.GetBuildingPermitsReport) => {
        return this.reportsService.getBuildingPermits(action.clip, action.apn, action.countyId).pipe(
          catchError(() => of(void(0))),
          map((report: BuildingPermitsReport) => {
            return new ReportsActions.GetBuildingPermitsReportSuccess(report);
          })
        );
      }, ActionName.GET_REPORT)
    ));

  checkFreeCreditEligibility$: Observable<ReportsActions.CheckFreeCreditEligibilitySuccess | ReportsActions.CheckFreeCreditEligibilityFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.CHECK_FREE_CREDIT_ELIGIBILITY),
      switchMap((action: ReportsActions.CheckFreeCreditEligibility) => {
        return this.reportsService.checkFreeCreditEligibility(action.featureCode, action.apn, action.productId).pipe(
          map((eligibility: FreeCreditEligibility) => new ReportsActions.CheckFreeCreditEligibilitySuccess(eligibility)),
          catchError(() => of(new ReportsActions.CheckFreeCreditEligibilityFailure()))
        );
      })
    ));

  sharePropertyIntelligence$: Observable<boolean> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ReportsActions.SHARE_PROPERTY_INTELLIGENCE),
      withLatestFrom(this.store$.pipe(select(selectPropIntelFilters))),
      this.spinnerOverlayService.wrapRequest(([action, filters]: [ReportsActions.SharePropertyIntelligence, Array<PropertyIntelligenceFilter>]) => {
        const propertyIntelligenceSearch: PropertyIntelligenceSearch = {
          propertyIntelligenceSearchFilters: filters.filter((filter: PropertyIntelligenceFilter) => filter.isSelected),
          chartKey: action.payload.chartType,
          searchType: action.payload.sharingOption
        };

        return this.propertyIntelligenceService.sharePdf(propertyIntelligenceSearch).pipe(
          catchError(() => of(void (0))),
          switchMap((response: Chart) => {
            const chartItems = response.propertyIntelligenceChartItems.slice();
            const labels = response.labels.slice();

            const isPercentageChart = ['HPI', 'HPI_FORECAST'].includes(action.payload.sharingOption);

            const chart = 'line' === 'line' ?
              this.propertyIntelligencePdfService.generateLineChart(chartItems, labels, false, isPercentageChart) :
              this.propertyIntelligencePdfService.generateBarChart(chartItems, labels, false, isPercentageChart);
            const chartTitle = action.payload.chartLabel;

            const propertyIntelligencePdfRequest: PropertyIntelligencePdfRequest = {
              chartCategory: action.payload.sharingOptionLabel,
              chartTitle: this.getChartTitle(chartTitle, filters[0].yearRange),
              chartData: chart,
              fileName: `${this.propertyIntelligencePdfService.getPdfTitle(chartTitle)}.pdf`,
              legendColors: {},
              legendLabels: {},
              standaloneShare: true,
              reportTitle: action.payload.chartLabel
            };

            chartItems.forEach(item => {
              propertyIntelligencePdfRequest.legendColors[item.title] = `background: ${item.color}`;
              propertyIntelligencePdfRequest.legendLabels[item.title] = item.label;
            });

            return of({ propertyIntelligencePdfRequest: propertyIntelligencePdfRequest, type: action.payload.type, email: action.payload.email });
          })
        );
      }, ActionName.SHARE_PROPERTY_INTELLIGENCE),
      switchMap((response: { propertyIntelligencePdfRequest: PropertyIntelligencePdfRequest, type: string, email: Email }) => {

        if(response.type === 'pdf') {
          this.store$.dispatch(new DownloadPropertyIntelligenceChartPdf(response.propertyIntelligencePdfRequest));
        } else if (response.type === 'email') {
          this.store$.dispatch(new EmailPropertyIntelligenceChart(response.propertyIntelligencePdfRequest, response.email));
        }

        return of(true);
      })
    );
  }, {dispatch: false});

  disablePropertyIntelligenceWalkthrough$: Observable<any> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.DISABLE_PROPERTY_INTELLIGENCE_WALKTHROUGH),
      this.spinnerOverlayService.wrapRequest((_action: ReportsActions.DisablePropertyIntelligenceWalkthrough) => {
        return this.propertyIntelligenceService.disableWalkthrough();
      }, ActionName.DISABLE_PROPERTY_INTELLIGENCE_WALKTHROUGH)
    ),
    { dispatch: false }
  );

  loadReportUsageSummary$: Observable<ReportsActions.LoadReportUsageSummarySuccess | ReportsActions.LoadReportUsageSummaryFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.LOAD_REPORT_USAGE_SUMMARY),
      switchMap(() =>
        this.reportsService.getAllReportUsageSummary().pipe(
          map((summary) => new ReportsActions.LoadReportUsageSummarySuccess(summary)),
          catchError(() => of(new ReportsActions.LoadReportUsageSummaryFailure()))
        )
      )
    )
  );

  loadMlsCreditStatus$: Observable<ReportsActions.LoadMlsCreditStatusSuccess | ReportsActions.LoadMlsCreditStatusFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.LOAD_MLS_CREDIT_STATUS),
      switchMap((action: ReportsActions.LoadMlsCreditStatus) =>
        this.reportsService.getMlsCreditStatus(action.payload.apn).pipe(
          map((status) => new ReportsActions.LoadMlsCreditStatusSuccess(status)),
          catchError(() => of(new ReportsActions.LoadMlsCreditStatusFailure()))
        )
      )
    )
  );

  refreshUsageSummaryOnPremiumReportAccess$: Observable<ReportsActions.LoadReportUsageSummary> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ReportsActions.GET_HAZARD_REPORT_SUCCESS,
        ReportsActions.GET_FLOOD_MAP_REPORT_SUCCESS,
        ReportsActions.GET_BUILDING_PERMITS_REPORT_SUCCESS,
        ReportsActions.GET_COMMUNITY_INSIGHTS_PDF_LINKS_SUCCESS
      ),
      map(() => new ReportsActions.LoadReportUsageSummary())
    )
  );

  fetchAiSummary$: Observable<ReportsActions.FetchAiSummarySuccess | ReportsActions.FetchAiSummaryFailure> = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.FETCH_AI_SUMMARY),
      exhaustMap((action: ReportsActions.FetchAiSummary) =>
        this.reportsService.getAiSummaryContent(action.payload.propertyId, action.payload.propertyDetailsJson).pipe(
          map((response) => {
            if (response.error) {
              return new ReportsActions.FetchAiSummaryFailure(response.error);
            }
            return new ReportsActions.FetchAiSummarySuccess(response.summary ?? '');
          }),
          catchError(() => of(new ReportsActions.FetchAiSummaryFailure('Failed to fetch summary content. Please try again later.')))
        )
      )
    )
  );

  trackAiSummaryToggle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_AI_SUMMARY_TOGGLE),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyInformation))),
      tap(([action, propertyInformation]: [ReportsActions.TrackAiSummaryToggle, PropertyInformation]) => {
        this.mixpanelService.track('AI Summary - Section Toggle', {
          action: action.payload.isExpanded ? 'expand' : 'collapse',
          section: 'AI Summary',
          propertyIdentifier: propertyInformation?.propertyIdentifier?.parcelId,
          fipsCode: propertyInformation?.propertyIdentifier?.fipsCode,
          apn: propertyInformation?.propertyIdentifier?.apn,
        });
      })
    ), { dispatch: false }
  );

  trackAiSummaryDwellTime$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_AI_SUMMARY_DWELL_TIME),
      withLatestFrom(this.store$.pipe(select(selectSelectedPropertyInformation))),
      tap(([action, propertyInformation]: [ReportsActions.TrackAiSummaryDwellTime, PropertyInformation]) => {
        this.mixpanelService.track('AI Summary Viewed', {
          'Duration Seconds': action.payload.durationSeconds,
          propertyIdentifier: propertyInformation?.propertyIdentifier?.parcelId,
          fipsCode: propertyInformation?.propertyIdentifier?.fipsCode,
          apn: propertyInformation?.propertyIdentifier?.apn,
        });
      })
    ), { dispatch: false }
  );

  trackEcomUpgradeNow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_ECOM_UPGRADE_NOW),
      tap((action: ReportsActions.TrackEcomUpgradeNow) => {
        this.mixpanelService.track('Ecom - Upgrade Now', {
          reportName: action.payload.reportName,
        });
      })
    ), { dispatch: false }
  );

  trackEcomUseCredit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_ECOM_USE_CREDIT),
      tap((action: ReportsActions.TrackEcomUseCredit) => {
        this.mixpanelService.track('Ecom - Use Credit', {
          availableCredits: action.payload.availableCredits,
        });
      })
    ), { dispatch: false }
  );

  trackEcomUpgradePackage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_ECOM_UPGRADE_PACKAGE),
      tap(() => {
        this.mixpanelService.track('Ecom - Upgrade Package');
      })
    ), { dispatch: false }
  );

  trackEcomManageSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_ECOM_MANAGE_SUBSCRIPTION),
      tap(() => {
        this.mixpanelService.track('Ecom - Manage Subscription');
      })
    ), { dispatch: false }
  );

  trackEcomUpgradeReportsMenu$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ReportsActions.TRACK_ECOM_UPGRADE_REPORTS_MENU),
      tap(() => {
        this.mixpanelService.track('Ecom - Upgrade Reports Menu');
      })
    ), { dispatch: false }
  );

  private getChartTitle(chartTitle: string, years: number): string {
    const yearSuffix = years === 1 ? 'year' : 'years';

    return `${chartTitle} (Last ${years} ${yearSuffix})`;
  }

  private handleCustomizedReport(reportType: ReportType):
    (source: Observable<FileLink>) => Observable<DownloadReportSuccess | DownloadReportFailure> {
    return (source: Observable<FileLink>) => {
      return source.pipe(
        takeUntil(this.actions$.pipe(ofType(ReportsActions.CANCEL_REPORTS_DOWNLOAD))),
        map((fileLink: FileLink) => fileLink && fileLink.fileLink
          ? new DownloadReportSuccess(fileLink)
          : new DownloadReportFailure({...fileLink, reportType})
        ),
        catchError(() => of(new DownloadReportFailure({reportType, fileLink: ''})))
      );
    };
  }

  private confirmDownload(response: FileLink): Observable<boolean> {
    const modalData = {
      title: 'Print',
      confirmButtonLabel: 'Print',
      messageTitle: 'Click the print button to download the PDF report',
      message: '',
      messageType: ConfirmationMessageType.INFO,
    };

    const afterClosed$ = this.shouldUseEnsembleModals()
      ? this.dialog
          .open(ConfirmationModalEnsembleComponent, {
            label: modalData.title,
            data: modalData,
          })
          .afterClosed()
      : this.confirmationService.openConfirmationModal(modalData);

    return afterClosed$.pipe(
      filter<boolean>(Boolean),
      tap(() => this.downloadPdfReport(response))
    );
  }

  private downloadPdfReport(response: FileLink): void {
    if (response && response.fileLink) {
      ReportUtils.downloadPdf(response);
    } else {
      this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'PDF report cannot be generated. Please try again later.');
    }
  }

  private rateLimitCheck(
    payload: any,
    retryActionFactory: (payload: any) => Action = (p) => new GetFloodMapReport(p)
  ): (source: Observable<any>) => Observable<any> {
    return (source) => {
      return source.pipe(
        tap((data) => {
          if (data && data.header.status === '429') {
            this.spinnerOverlayService.hideSpinner(ActionName.GET_REPORT);
            this.store$.dispatch(new SetReportRateLimitType(REPORT_RATE_LIMIT.floodReport));
          }
        }),
        delayWhen((data) => {
          return data && data.header.status === '429' ? timer(RATE_LIMIT_RETRY_TIME_SECONDS * 1000) : timer(0);
        }),
        tap((data) => {
          if (data && data.header.status === '429') {
            this.spinnerOverlayService.showSpinner(ActionName.GET_REPORT);
            this.store$.dispatch(retryActionFactory(payload));
          }
        })
      );
    };
  }

  private filterCached<T>(cachedData$: Observable<LoadableData>): (source: Observable<T>) => Observable<T> {
    return (source: Observable<T>) => {
      return source.pipe(
        withLatestFrom(cachedData$),
        filter(([_, cachedData]: [T, LoadableData]) => {
          return !cachedData || !cachedData.isLoaded && cachedData.hasOwnProperty('isLoaded');
        }),
        map(([action, _]: [T, LoadableData]) => action)
      );
    };
  }

  private emailReport<T>(
    project: (value: T) => Observable<FileLink>
  ): (source: Observable<T>) => Observable<SendEmail> {
    return (source: Observable<T>) => {
      return source.pipe(
        withLatestFrom(
          this.store$.pipe(select(selectSelectedPropertyInformation)),
          this.store$.pipe(select(selectUserName)),
          this.store$.pipe(select(selectPreferenceValue(PreferenceCode.PG_RL_GENERAL_PREFERENCES, PreferenceCode.PG_GENPREF_USER_EMAIL)))
        ),
        switchMap(([action, subjectProperty, userName, replyTo]: [T, PropertyInformation, string, string]) => {
          this.spinnerOverlayService.hideSpinner(ActionName.HAZARD);
          const emailModalData = {
            subject: 'Realist Report',
            replyTo,
            messageBody: this.getBodyText(userName, subjectProperty),
          };

          const afterClosed$ = this.shouldUseEnsembleModals()
            ? this.dialog
              .open(EmailModalEnsembleComponent, {
                modalClass: 'ensemble-dialog--email',
                label: 'Email Report',
                data: {
                  title: 'Email',
                  ...emailModalData,
                }
              })
              .afterClosed()
            : this.legacyDialog
              .open(EmailModalComponent, {
                panelClass: ModalType.SMALL,
                data: emailModalData,
              })
              .afterClosed();

          return afterClosed$
            .pipe(
              filter(Boolean),
              this.spinnerOverlayService.wrapRequest((emailData: Email) => {
                return project(action)
                  .pipe(
                    catchError(() => of(void (0))),
                    filter(Boolean),
                    map((fileLink: FileLink) => {
                      const fileName = ReportUtils.getFileName(fileLink.fileLink);
                      const reportType = fileLink.reportType;
                      return new SendEmail({...emailData, fileName, reportType});
                    })
                  );
              }, ActionName.EMAIL_REPORT)
            );
        }),
      );
    };
  }

  private copyElement(element: PreferenceElement) {
    return {
      ...element,
      preferenceValue: {
        ...element.preferenceValue,
        values: [...element.preferenceValue.values]
      }
    };
  }

  private getBodyText(userName: string, subjectProperty: PropertyInformation): string {
    const emailLeftSpace = '&nbsp;&nbsp;&nbsp;&nbsp;';
    let reportBodyText = 'The report was e-mailed to you from '
      + userName
      + '<br/><br/>'
      + emailLeftSpace
      + 'For the property located at:'
      + '<br/>';

    if (subjectProperty) {
      const subjectPropertyData = subjectProperty.propertyData;
      const propertyLocation: string = this.formatterService.getPropertyAddress(subjectPropertyData);
      if (propertyLocation.length > 0) {
        reportBodyText = reportBodyText
          + emailLeftSpace
          + propertyLocation
          + '<br/>'
          + emailLeftSpace
          + this.formatterService.getAddressDetails(subjectPropertyData)
          + '<br/>';
      } else {
        const county: string = subjectPropertyData.COUNTY;
        const fipsCode: string = subjectProperty.propertyIdentifier && subjectProperty.propertyIdentifier.fipsCode;
        const countySuffix: string = this.formatterService.getShowCountySuffix(fipsCode, county) ? COUNTY_SUFFIX : '';

        reportBodyText = reportBodyText
          + emailLeftSpace
          + this.formatterService.getNonGeoCodedPropertyTitle(subjectPropertyData, countySuffix, true)
          + '<br/>';
      }
    }
    return reportBodyText;
  }

  private mixPanelServiceTrackingDownloadReports(reportTab: string, eventId: string, propertyInformation: PropertyInformation) {
    this.mixpanelService.track(eventId, {
      reportTab: reportTab,
      propertyIdentifier: propertyInformation.propertyIdentifier.parcelId,
      fipsCode: propertyInformation.propertyIdentifier.fipsCode,
      mlsStatus: ReportsMixPanelUtil.getMlsListingStatus(propertyInformation.propertyData),
    });
  }

  private disableButtons() {
    this.store$.dispatch(new SetSubHeaderButtons({
      quickPrint: {
        show: false
      },
      customizePrint: {
        show: false
      },
      quickEmail: {
        show: false
      },
      customizeEmail: {
        show: false
      },
      customizeView: {
        show: false
      },
      searchCriteriaView: {
        show: false
      },
      transactionDesk: {
        show: false
      },
      valueMap: {
        show: false
      },
      comparablesGenerate: {
        show: false
      },
      comparablesEdit: {
        show: false
      },
      hideEmptyFields: {
        show : false,
      }
    }));
  }

  private resolvePropertyIdentifierApn(propertyIdentifier: PropertyIdentifier, subheaderHeader?: ReportHeader): PropertyIdentifier {
    if (!propertyIdentifier) {
      return propertyIdentifier;
    }
    const apn = propertyIdentifier.apn;
    if (!apn || apn === 'N') {
      return {
        ...propertyIdentifier,
        apn: subheaderHeader?.apn || null
      };
    }
    return propertyIdentifier;
  }

  private getDocumentNumber(payload: string): any {
    const params = payload.split('&');
    const paramsMap = new Map();

    if(params) {
      params.forEach(param => {
        const pair = param.split('=');

        const key = decodeURIComponent(pair[0]);
        let value = '';

        if(pair.length > 1) {
          value = decodeURIComponent(pair[1]);
        }

        paramsMap.set(key, value);
      });
    }

    return paramsMap.get('doc1') ? paramsMap.get('doc1') : paramsMap.get('doc2');
  }
}
